import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle2, XCircle, AlertTriangle, Save, Sparkles, FileText, Download, PenLine, Loader2 } from "lucide-react";
import { ouvrirPdf } from "@/pages/Devis";
import { CATEGORIES, ChampsType, Choice, F } from "@/components/ContratCreateDialog";

const EDIT = ["poste", "qualification", "convention_collective", "date_debut", "lieux_travail", "caisse_retraite", "prevoyance", "salarie_adresse", "employeur_signataire"];
const LIB = { poste: "Poste", qualification: "Qualification", convention_collective: "Convention collective", date_debut: "Date de début",
  lieux_travail: "Lieux d'exécution", caisse_retraite: "Caisse de retraite", prevoyance: "Prévoyance", salarie_adresse: "Adresse du salarié",
  employeur_signataire: "Signataire employeur" };

function Checks({ items }) {
  return (
    <ul className="space-y-1" data-testid="contrat-verification">
      {items.map((x) => (
        <li key={x.code} className="flex gap-2 text-xs" data-testid={`contrat-check-${x.code}`}>
          {x.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" /> : x.bloquant ? <XCircle className="h-3.5 w-3.5 text-red-600 shrink-0" />
            : <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
          <span className={x.ok ? "text-slate-600" : "text-slate-900"}>{x.libelle}{x.detail ? ` — ${x.detail}` : ""} <span className="text-slate-400">({x.base})</span></span>
        </li>
      ))}
    </ul>
  );
}

export function ContratDetail({ contrat, role, onChange }) {
  const [c, setC] = useState(contrat);
  const signed = new Set((c.signatures || []).map((s) => s.role));
  const [busy, setBusy] = useState("");
  const [dirty, setDirty] = useState(false);
  const brouillon = !c.content_hash && ["admin", "gestionnaire_ge"].includes(role);
  const set = (k) => (v) => setC((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const call = async (key, fn, ok) => {
    setBusy(key);
    try { const { data } = await fn(); setC(data); setDirty(false); onChange(data); if (ok) toast.success(ok); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } finally { setBusy(""); }
  };
  const save = () => {
    const keys = [...EDIT, "categorie", "motif_cdd", "motif_precision", "salarie_remplace", "date_fin", "repartition_horaire", "heures_complementaires"];
    const body = Object.fromEntries(keys.filter((k) => c[k] != null && c[k] !== "").map((k) => [k, c[k]]));
    ["duree_hebdo", "salaire_horaire", "periode_essai_jours"].forEach((k) => { if (c[k] !== "" && c[k] != null) body[k] = Number(c[k]); });
    return call("save", () => api.patch(`/contrats/${c.id}`, dirty ? { ...body, articles: c.articles } : body), "Brouillon enregistré");
  };
  const [nom, setNom] = useState(["admin", "gestionnaire_ge"].includes(role) ? contrat.employeur_signataire || "" : contrat.salarie_nom || "");
  const signer = (r) => {
    if (nom.trim().length < 2) return toast.error("Saisissez le nom du signataire");
    call("sign", () => api.post(`/contrats/${c.id}/signer`, { role: r, nom: nom.trim() }), "Signature enregistrée");
  };
  const peutSigner = ["a_signer", "en_signature"].includes(c.statut) &&
    ((["admin", "gestionnaire_ge"].includes(role) && !signed.has("employeur")) || (role === "salarie" && !signed.has("salarie")));
  return (
    <div className="space-y-5" data-testid={`contrat-detail-${c.id}`}>
      <div className="flex flex-wrap gap-2">
        {brouillon && <Button size="sm" onClick={save} disabled={!!busy} data-testid="contrat-save"><Save className="h-3.5 w-3.5 mr-1" />Enregistrer</Button>}
        {brouillon && <Button size="sm" variant="outline" disabled={!!busy} data-testid="contrat-rediger"
          onClick={() => call("redige", () => api.post(`/contrats/${c.id}/rediger`, {}, { timeout: 120000 }), "Articles réécrits par ZOE")}>
          {busy === "redige" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}Faire réécrire par ZOE</Button>}
        {brouillon && <Button size="sm" variant="outline" disabled={!!busy || c.bloquants > 0} data-testid="contrat-generer-pdf"
          onClick={() => call("pdf", () => api.post(`/contrats/${c.id}/pdf`), "PDF archivé en GED — contrat ouvert à la signature")}>
          <FileText className="h-3.5 w-3.5 mr-1" />Générer le PDF et ouvrir à la signature</Button>}
        {c.statut !== "brouillon" && <Button size="sm" variant="outline" data-testid="contrat-telecharger"
          onClick={() => ouvrirPdf(`/contrats/${c.id}/pdf`, `${c.numero}.pdf`).catch(() => toast.error("PDF indisponible"))}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>}
        {peutSigner && <Input className="h-8 w-48" placeholder="Nom du signataire" value={nom} onChange={(e) => setNom(e.target.value)} data-testid="contrat-signataire-nom" />}
        {["a_signer", "en_signature"].includes(c.statut) && ["admin", "gestionnaire_ge"].includes(role) && !signed.has("employeur") &&
          <Button size="sm" onClick={() => signer("employeur")} data-testid="contrat-signer-employeur"><PenLine className="h-3.5 w-3.5 mr-1" />Signer pour l'employeur</Button>}
        {["a_signer", "en_signature"].includes(c.statut) && role === "salarie" && !signed.has("salarie") &&
          <Button size="sm" onClick={() => signer("salarie")} data-testid="contrat-signer-salarie"><PenLine className="h-3.5 w-3.5 mr-1" />Signer mon contrat</Button>}
      </div>
      {brouillon && c.statut === "a_signer" && <p className="text-xs text-amber-700" data-testid="contrat-warning-a-signer">Ce contrat est déjà ouvert à la signature : toute modification le repasse en brouillon et le PDF devra être régénéré.</p>}
      {c.bloquants > 0 && <p className="text-xs text-red-700" data-testid="contrat-bloquants">{c.bloquants} mention(s) obligatoire(s) à compléter avant le PDF.</p>}
      <Checks items={c.verification || []} />
      {brouillon && (
        <div className="grid sm:grid-cols-2 gap-3 rounded-lg border border-slate-200 p-4">
          {EDIT.map((k) => <F key={k} label={LIB[k]}><Input type={k === "date_debut" ? "date" : "text"} value={c[k] || ""} onChange={set(k)} data-testid={`contrat-edit-${k}`} /></F>)}
          <F label="Catégorie"><Choice value={c.categorie} onChange={set("categorie")} options={CATEGORIES} testid="contrat-edit-categorie" /></F>
          <F label="Durée hebdomadaire (h)"><Input type="number" value={c.duree_hebdo || ""} onChange={set("duree_hebdo")} data-testid="contrat-edit-duree_hebdo" /></F>
          <F label="Salaire brut horaire (€)"><Input type="number" value={c.salaire_horaire || ""} onChange={set("salaire_horaire")} data-testid="contrat-edit-salaire_horaire" /></F>
          <F label="Période d'essai (jours)"><Input type="number" value={c.periode_essai_jours || ""} onChange={set("periode_essai_jours")} data-testid="contrat-edit-periode_essai_jours" /></F>
          <ChampsType f={c} set={set} />
        </div>
      )}
      <div className="space-y-3">
        {(c.articles || []).map((a, i) => (
          <div key={i} className="rounded-lg border border-slate-200 p-3 bg-white" data-testid={`contrat-article-${i}`}>
            <div className="text-sm font-semibold text-slate-900 mb-1">Article {i + 1} — {a.titre}</div>
            {brouillon ? <Textarea rows={4} value={a.texte} data-testid={`contrat-article-texte-${i}`}
              onChange={(e) => { setDirty(true); setC((x) => ({ ...x, articles: x.articles.map((y, j) => (j === i ? { ...y, texte: e.target.value } : y)) })); }} />
              : <p className="text-sm text-slate-700 whitespace-pre-line">{a.texte}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
